import { useMemo, useState } from "react";
import { useAdminCatalogue, useAdminClients } from "@/api/useAdmin";
import { AdminHeader } from "./AdminHeader";
import { AdminKpis } from "./AdminKpis";
import { StatusPill } from "@/components/StatusPill";
import { StockBadge } from "@/components/StockBadge";
import { Button } from "@/components/Button";
import { cn } from "@/lib/cn";

/**
 * Catalogue admin — CRUD on kits with per-client visibility chips and an
 * active toggle (rendered, but Phase 1 wires read-only state; Phase 2
 * persists). Search by code or title, filter by active/draft.
 */
export function CatalogueAdminPage() {
  const { data: kits = [], isLoading } = useAdminCatalogue();
  const { data: clients = [] } = useAdminClients();
  const [q, setQ] = useState("");
  const [activeFilter, setActiveFilter] = useState<"all" | "active" | "draft">("all");

  const filtered = useMemo(
    () =>
      kits.filter(
        (k) =>
          (activeFilter === "all" || (activeFilter === "active" ? k.active : !k.active)) &&
          (!q || k.title.toLowerCase().includes(q.toLowerCase()) || k.code.toLowerCase().includes(q.toLowerCase())),
      ),
    [kits, q, activeFilter],
  );

  return (
    <div className="mx-auto max-w-[1480px] px-8 py-8">
      <AdminHeader
        eyebrow="Studio admin · Catalogue"
        title="Catalogue, pricing & visibility"
        subtitle="The kits clients can choose from in Studio. Each kit can be shown to specific clients only."
        actions={
          <Button className="bg-[var(--admin)] hover:bg-[var(--admin-dark)] active:bg-[var(--admin-dark)]">
            + New kit
          </Button>
        }
      />

      <AdminKpis />

      <div className="mb-3.5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="search"
            placeholder="Search by name or code…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="min-w-[260px] rounded border border-[var(--border-2)] bg-white px-3 py-2 text-sm outline-none focus-visible:border-fg-1 focus-visible:ring-2 focus-visible:ring-[var(--border-focus)]"
          />
          <select
            value={activeFilter}
            onChange={(e) => setActiveFilter(e.target.value as typeof activeFilter)}
            className="rounded border border-[var(--border-2)] bg-white px-3 py-2 text-sm outline-none focus-visible:border-fg-1 focus-visible:ring-2 focus-visible:ring-[var(--border-focus)]"
          >
            <option value="all">All kits</option>
            <option value="active">Active</option>
            <option value="draft">Draft / inactive</option>
          </select>
        </div>
        <div className="text-xs text-fg-3">
          {filtered.length} of {kits.length} kits
        </div>
      </div>

      {isLoading ? (
        <div className="text-fg-3">Loading…</div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((k) => (
            <article key={k.id} className="flex flex-col gap-2 rounded-lg border border-[var(--border-1)] bg-white p-4">
              <div className="flex items-baseline justify-between gap-2">
                <h3 className="m-0 text-base font-semibold text-fg-1">{k.title}</h3>
                <span className="font-mono text-xxs text-fg-3">{k.code}</span>
              </div>
              <p className="m-0 text-sm text-fg-3">{k.desc}</p>
              <div className="mt-1 flex flex-wrap gap-x-3.5 gap-y-1 text-xs text-fg-3">
                <span><strong className="text-fg-1">£{k.price.toFixed(2)}</strong> / kit</span>
                <span>{k.tube} · TAT {k.tat}</span>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <StatusPill status={k.active ? "active" : "draft"} />
                <StockBadge qty={k.stockQty} />
                {k.tag && <StatusPill status="proof" className="bg-[var(--admin-tint)] text-[var(--admin-dark)]" />}
              </div>

              <div className="mt-2 border-t border-[var(--border-1)] pt-2.5">
                <div className="mb-1.5 text-2xs font-semibold uppercase tracking-wider text-fg-3">
                  Visible to clients
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {clients.map((c) => {
                    const on = k.visibleTo.includes(c.id);
                    return (
                      <span
                        key={c.id}
                        className={cn(
                          "rounded-full border px-2 py-0.5 text-xxs font-medium",
                          on
                            ? "border-[var(--admin-border,var(--admin-tint))] bg-[var(--admin-tint)] text-[var(--admin-dark)]"
                            : "border-[var(--border-1)] bg-bg-2 text-fg-3",
                        )}
                      >
                        {on && "✓ "}{c.slug}
                      </span>
                    );
                  })}
                </div>
                <div className="mt-2 text-xxs text-fg-3">
                  {k.visibleTo.length === 0
                    ? "Not visible to anyone — clients won't see this kit."
                    : `${k.visibleTo.length} client${k.visibleTo.length === 1 ? "" : "s"}`}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
